import { Tooltip, Typography } from '@mui/material';
import { useConfig } from '@/context/ConfigProvider';
import { formatDateTime } from '@/utils';

/**
 * Which build is actually in front of you.
 *
 * Worth a permanent corner of the screen rather than a debug page: with continuous deploys to a
 * shared environment, "is my change live yet?" and "which build produced this bug?" are asked
 * constantly, and guessing from behaviour is how a fixed bug gets reported twice.
 *
 * The environment comes from `config.json` (fetched at runtime) and the version from the build, so
 * the two together also reveal a mismatch — a bundle promoted to an environment it was not built
 * for shows a different `envName` than the config, which is exactly the case worth noticing.
 */
const BuildInfo = () => {
  const config = useConfig();
  const { appVersion, gitShortSha, buildDate, envName } = __BUILD_INFO__;

  const builtFor = envName && envName !== config.env ? envName : undefined;

  return (
    <Tooltip
      title={
        <>
          <div>Version {appVersion}</div>
          <div>Commit {gitShortSha}</div>
          {buildDate && <div>Built {formatDateTime(buildDate)}</div>}
          {builtFor && <div>Built for {builtFor} — not this environment</div>}
        </>
      }
    >
      <Typography
        variant="caption"
        color={builtFor ? 'warning.main' : 'text.secondary'}
        sx={{ display: 'block', cursor: 'help' }}
      >
        {appVersion} · {gitShortSha}
        {builtFor ? ' ⚠' : ''}
      </Typography>
    </Tooltip>
  );
};

export default BuildInfo;
